import { getCurrentUserDetail } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Col,
  Flex,
  LegacyForm,
  Row,
  Skeleton,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { userApis } from "@payping/api";
import { USER_CLAIMS_LIST, useUserClaim } from "@/utils/useUserClaims";
import { useImmerAtom } from "jotai-immer";
import { coreAtom } from "@/core/state/root";
import { URLS } from "@payping/utilities-shared/consts";
import { useAsync } from "react-use";

const ShopSettings: React.FC = () => {
  const [, setCoreState] = useImmerAtom(coreAtom);

  const dispatch = useAppDispatch();
  const userDetail = useAppSelector((state) => state.user);
  const [form] = LegacyForm.useForm();
  const { t } = useAppTranslation();
  const { hasPermission } = useUserClaim([USER_CLAIMS_LIST.shopService]);

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async (value) => {
      if (value.shopSetting === true) {
        await userApis.requestShopEnamadWithCancellationTokenGet();
        await dispatch(getCurrentUserDetail());
      }
    },
    [dispatch],
    undefined,
  );

  useAsync(() => dispatch(getCurrentUserDetail()), [dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      shopSetting: userDetail.isShopEnamadRequested,
    });
  }, [form, userDetail]);

  return (
    <Flex vertical gap={"large"}>
      <Typography.Title level={4} className="tw:m-0!">
        {t("settings.shop.title")}
      </Typography.Title>

      <Row className={"tw:w-full"}>
        <Col lg={12} xl={16} className={"tw:flex tw:items-center"}>
          <LegacyForm form={form} onFinish={hasPermission ? onFinish : undefined}>
            <LegacyForm.Item>
              <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
                <Typography.Title className={"tw:mb-0!"} level={4}>
                  {t("settings.shop.shopActivation")}
                </Typography.Title>
                <Typography.Text>{t("settings.shop.description")}</Typography.Text>
              </Space>
            </LegacyForm.Item>
            <LegacyForm.Item name={"shopSetting"} valuePropName={"checked"}>
              {loading ? (
                <Skeleton.Input />
              ) : (
                <Checkbox disabled={userDetail.isShopEnamadRequested}>{t("settings.shop.successShowInfo")}</Checkbox>
              )}
            </LegacyForm.Item>
            <LegacyForm.Item noStyle dependencies={["shopSetting"]}>
              {({ getFieldValue }) => {
                const active = getFieldValue("shopSetting");

                return (
                  <Button
                    type={"primary"}
                    loading={loading}
                    disabled={!active || userDetail.isShopEnamadRequested}
                    htmlType={hasPermission ? "submit" : "button"}
                    onClick={() =>
                      hasPermission
                        ? undefined
                        : setCoreState((draft) => {
                            // eslint-disable-next-line no-param-reassign
                            draft.upgradeUserPanel.toStandard.service = "onlineShop";
                            // eslint-disable-next-line no-param-reassign
                            draft.upgradeUserPanel.toStandard.showModal = true;
                          })
                    }
                  >
                    {t("settings.shop.activate")}
                  </Button>
                );
              }}
            </LegacyForm.Item>
          </LegacyForm>
        </Col>
        <Col lg={12} xl={8} className={"tw:flex tw:justify-end"}>
          <img
            className="tw:h-[562px] tw:w-[288px] tw:object-cover"
            src={`${URLS.STORAGE_URL}/dashboard-app/shop.png`}
          />
        </Col>
      </Row>
    </Flex>
  );
};

export default ShopSettings;
